import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch } from "@/lib/api/client"
import type { Collection, ApiResource } from "@/lib/api/types"
import type {
  ApiFitnessAssessment,
  ApiFitnessQuestion,
  FitnessAssessment,
  FitnessQuestion,
} from "@/types/fitness"

function toFitnessQuestion(question: ApiFitnessQuestion): FitnessQuestion {
  return {
    key: question.key,
    title: question.title,
    question: question.question,
    help: question.help,
    value:
      question.value === null ? null : question.value === "yes" ? "Yes" : "No",
    detail: question.detail,
    disclosure: question.disclosure,
    answeredAt: question.answered_at,
    answeredForTopic: question.answered_for_topic,
  }
}

export function toFitnessAssessment(
  data: ApiFitnessAssessment
): FitnessAssessment {
  return {
    id: data.id,
    status: data.status,
    periodKey: data.period_start,
    periodStart: data.period_start,
    periodEnd: data.period_end,
    dueOn: data.due_on,
    templateVersion: data.question_set_version,
    progress: data.progress,
    standing: data.standing,
    outcome: data.outcome,
    actions: data.actions,
    sections: (data.sections ?? []).map((section) => ({
      key: section.key,
      label: section.label,
      questions: section.questions.map(toFitnessQuestion),
    })),
    topics: (data.topics ?? []).map((topic) => ({
      key: topic.key,
      question: topic.question,
      help: topic.help,
      answer: topic.answer,
      lastYear: topic.last_year,
      questions: topic.questions.map(toFitnessQuestion),
    })),
    issues: null,
    stands: data.stands,
    signedOn: data.signed_on,
    nextReviewOn: data.next_review_on,
    declaration: data.declaration,
    submittedAt: data.submitted_at,
    createdAt: data.created_at,
    updatedAt: data.updated_at,
  }
}

function path(individualId: string, assessmentId?: string): string {
  return assessmentId
    ? `/individuals/${individualId}/fit-and-proper/${assessmentId}`
    : `/individuals/${individualId}/fit-and-proper`
}

export const fitnessQueries = {
  list: (individualId: string) =>
    queryOptions({
      queryKey: apiKey(path(individualId)),
      queryFn: async ({ signal }): Promise<Collection<FitnessAssessment>> => {
        const { data } = await apiFetch<{ data: ApiFitnessAssessment[] }>(
          path(individualId),
          { signal }
        )

        return { items: data.map(toFitnessAssessment) }
      },
    }),
  detail: (individualId: string, assessmentId: string) =>
    queryOptions({
      queryKey: apiKey(path(individualId, assessmentId)),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<ApiFitnessAssessment>>(
          path(individualId, assessmentId),
          { signal }
        )

        return toFitnessAssessment(data)
      },
    }),
}

function useStore(individualId: string) {
  const queryClient = useQueryClient()

  return (assessment: FitnessAssessment) => {
    queryClient.setQueryData(
      apiKey(path(individualId, assessment.id)),
      assessment
    )
  }
}

function useFitnessAction<TBody extends Record<string, unknown>>(
  individualId: string,
  suffix: string
) {
  const store = useStore(individualId)

  return useMutation({
    mutationFn: async (body: TBody) => {
      const { data } = await apiFetch<ApiResource<ApiFitnessAssessment>>(
        `${path(individualId)}${suffix}`,
        { method: "POST", body }
      )

      return toFitnessAssessment(data)
    },
    onSuccess: store,
  })
}

export function useOpenFitnessAssessment(individualId: string) {
  return useFitnessAction<Record<string, never>>(individualId, "")
}

export function useAnswerFitnessTopic(
  individualId: string,
  assessmentId: string
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: {
      topicKey: string
      disclosures: { question_key: string; detail: string }[]
    }) => {
      const { data } = await apiFetch<ApiResource<ApiFitnessAssessment>>(
        `${path(individualId, assessmentId)}/topics/${input.topicKey}`,
        { method: "PUT", body: { disclosures: input.disclosures } }
      )

      return toFitnessAssessment(data)
    },
    onSuccess: (assessment) => {
      queryClient.setQueryData(
        apiKey(path(individualId, assessmentId)),
        assessment
      )
    },
  })
}

export function useSubmitFitnessAssessment(
  individualId: string,
  assessmentId: string
) {
  return useFitnessAction<{ signed_on?: string }>(
    individualId,
    `/${assessmentId}/submit`
  )
}

export function useDeleteFitnessAssessment(individualId: string) {
  return useMutation({
    mutationFn: async (assessmentId: string) => {
      await apiFetch(path(individualId, assessmentId), { method: "DELETE" })
    },
  })
}
